import { AppCard } from '@/components/ui/AppCard';
import AppTable from '@/components/ui/AppTable';
import { endpoints } from '@/services/endpoints';
import { axiosGetRequest, axiosDeleteRequest } from '@/services/Request';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userDataFormatter } from './helpers';
import { useState, useRef } from 'react';
import AppRemoveModal from '@/components/ui/AppRemoveModal';
import { IUserData } from '@/types';
import { toast } from 'react-toastify';
import AddUserModal from './AddUserModal';
import { TableFilterBar, type FilterConfig } from '@/components/ui/TableFilterBar';
import { SendNotificationModal } from '@/pages/Notifications/Notifications';

const FILTER_CONFIGS: FilterConfig[] = [
  {
    key: 'isActive',
    label: 'Status',
    options: [
      { value: '', label: 'All Statuses' },
      { value: 'true', label: 'Active' },
      { value: 'false', label: 'Inactive' },
    ],
  },
  {
    key: 'isVerified',
    label: 'Verified',
    options: [
      { value: '', label: 'All' },
      { value: 'true', label: 'Verified' },
      { value: 'false', label: 'Unverified' },
    ],
  },
  {
    key: 'isBlocked',
    label: 'Blocked',
    options: [
      { value: '', label: 'All' },
      { value: 'true', label: 'Blocked' },
      { value: 'false', label: 'Not Blocked' },
    ],
  },
  {
    key: 'role',
    label: 'Role',
    options: [
      { value: '', label: 'All Roles' },
      { value: 'user', label: 'User' },
      { value: 'editor', label: 'Editor' },
      { value: 'admin', label: 'Admin' },
      { value: 'superAdmin', label: 'Super Admin' },
    ],
  },
  {
    key: 'gender',
    label: 'Gender',
    options: [
      { value: '', label: 'All Genders' },
      { value: 'male', label: 'Male' },
      { value: 'female', label: 'Female' },
    ],
  },
  {
    key: 'provider',
    label: 'Provider',
    options: [
      { value: '', label: 'All Providers' },
      { value: 'google', label: 'Google' },
      { value: 'phone', label: 'Phone' },
    ],
  },
];

const EMPTY_FILTERS = {
  isActive: '',
  isVerified: '',
  isBlocked: '',
  role: '',
  gender: '',
  provider: '',
};

function Users() {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>(EMPTY_FILTERS);
  const [userToDelete, setUserToDelete] = useState<IUserData | null>(null);
  const [notifyUser, setNotifyUser] = useState<IUserData | null>(null);
  const addUserRef = useRef<HTMLDialogElement>(null);
  const queryClient = useQueryClient();

  // Build active query params — only send truthy filter values
  const activeParams = Object.fromEntries(Object.entries(filterValues).filter(([, v]) => v !== ''));

  const {
    data: usersData,
    isLoading,
    isSuccess,
  } = useQuery({
    queryKey: [endpoints.getAllUsers, page, searchTerm, filterValues],
    queryFn: () =>
      axiosGetRequest(endpoints.getAllUsers, {
        limit: 10,
        page,
        keyword: searchTerm || undefined,
        ...activeParams,
      }),
    enabled: true,
  });

  const deleteUser = useMutation({
    mutationFn: (id: string) => axiosDeleteRequest(endpoints.adminDeleteUser, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllUsers] });
      toast.success('User deleted successfully');
      setUserToDelete(null);
    },
    onError: () => {
      toast.error('Failed to delete user');
    },
  });

  const handlePages = (pageNumber: number) => setPage(pageNumber);

  function handleSearch(search: string) {
    setSearchTerm(search);
    setPage(1);
  }

  function handleFilterChange(key: string, value: string) {
    setFilterValues(prev => ({ ...prev, [key]: value }));
    setPage(1);
  }

  function handleClearAll() {
    setFilterValues(EMPTY_FILTERS);
    setPage(1);
  }

  const handleDelete = (user: IUserData) => setUserToDelete(user);

  return (
    <div>
      <AddUserModal ref={addUserRef} />
      <AppCard>
        <TableFilterBar
          filters={FILTER_CONFIGS}
          values={filterValues}
          onChange={handleFilterChange}
          onClearAll={handleClearAll}
          className="mb-4 border-b border-coal-100 pb-4"
        />
        <AppTable
          tableButtonContent="Add User"
          modalRef={addUserRef?.current}
          hasPagination
          header={[
            { name: 'User Name', key: 'fullName' },
            { name: 'Active', key: 'isActive' },
            { name: 'Verified', key: 'isVerified' },
            { name: 'Phone Verified', key: 'isPhoneNumberVerified' },
            { name: 'Blocked', key: 'isBlocked' },
            { name: 'Provider', key: 'provider' },
            { name: 'Gender', key: 'gender' },
            { name: 'Role', key: 'role' },
            { name: 'Actions', key: 'actions' },
          ]}
          handleSearch={handleSearch}
          currentPage={page}
          totalPages={usersData?.pagination?.totalPages}
          handlePages={handlePages}
          data={
            (isSuccess &&
              userDataFormatter(usersData.result, handleDelete, user => setNotifyUser(user))) ||
            []
          }
          isLoading={isLoading}
        />
      </AppCard>

      {userToDelete && (
        <AppRemoveModal
          onClose={() => setUserToDelete(null)}
          onSubmit={() => deleteUser.mutate(userToDelete._id)}
          title="Delete User"
          text={`Are you sure you want to delete "${userToDelete.fullName}"? This action cannot be undone.`}
        />
      )}

      {/* Send push notification to a specific user from the table */}
      {notifyUser && (
        <SendNotificationModal
          onClose={() => setNotifyUser(null)}
          targetUserId={notifyUser._id}
          targetUserName={notifyUser.fullName}
        />
      )}
    </div>
  );
}
export default Users;
